import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { Editor } from '@components/shared/HtmlEditor/HtmlEditor';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferDetails, RequestType } from '../types';

interface SendOfferModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSendOffer: (offerData: SendOfferData) => Promise<void>;
	onEditOffer?: (offerData: SendOfferData) => Promise<void>;
	onCancelOffer?: () => Promise<void>;
	onChat?: () => void;
	currency?: string;
	initialPrice?: number;
	isEdit?: boolean;
	requestType: RequestType;
	isSending?: boolean;
	offerDetails?: OfferDetails | null;
	forceCancelDialogClose?: boolean;
}

export interface SendOfferData {
	price: number;
	currency: string;
	message: string;
	isFree?: boolean;
	isFixedPrice?: boolean;
}

// Define the different price options for ONE_TIME
enum PriceOption {
	FIXED = 'FIXED',
	CUSTOM = 'CUSTOM',
	FREE = 'FREE',
}

// Currency options
const CURRENCY_OPTIONS = [
	{ value: 'EUR', label: 'EUR' },
	{ value: 'USD', label: 'USD' },
];

export const SendOfferModal: React.FC<SendOfferModalProps> = ({
	isOpen,
	onClose,
	onSendOffer,
	onEditOffer,
	onCancelOffer,
	onChat,
	currency = 'EUR',
	initialPrice,
	isEdit = false,
	requestType,
	isSending = false,
	offerDetails = null,
	forceCancelDialogClose = false,
}) => {
	const { t } = useTranslation();
	const [price, setPrice] = useState<number | string>('');
	const [netAmount, setNetAmount] = useState<number | string>('');
	const [serviceFee, setServiceFee] = useState<number | string>('');
	const [message, setMessage] = useState<string>('');
	const [selectedPriceOption, setSelectedPriceOption] = useState<PriceOption>(PriceOption.FIXED);
	const [showCancelConfirmation, setShowCancelConfirmation] = useState<boolean>(false);
	const [fixedPrice] = useState<number>(20); // Fixed price of 20 EUR
	const [selectedCurrency, setSelectedCurrency] = useState<string>(currency);
	const [priceError, setPriceError] = useState<string>('');
	const [netAmountError, setNetAmountError] = useState<string>('');

	const isOneTime = requestType === RequestType.ONE_TIME;
	
	// Effect to close the cancel confirmation dialog when forceCancelDialogClose is true
	useEffect(() => {
		if (forceCancelDialogClose && showCancelConfirmation) {
			setShowCancelConfirmation(false);
		}
	}, [forceCancelDialogClose]);

	// Initialize values on open
	useEffect(() => {
		if (isOpen) {
			// If initialPrice is provided, use it; otherwise leave fields empty
			if (initialPrice !== undefined && initialPrice !== null) {
				const numericInitialPrice = typeof initialPrice === 'string' ? parseFloat(initialPrice) : Number(initialPrice);
				
				// Only set price if it's a positive number
				if (!isNaN(numericInitialPrice) && numericInitialPrice > 0) {
					setPrice(numericInitialPrice);
					
					// Calculate service fee (18% of price)
					const calculatedFee = numericInitialPrice * 0.18;
					setServiceFee(parseFloat(calculatedFee.toFixed(2)));
					
					// Calculate net amount (82% of price)
					const calculatedNet = numericInitialPrice * 0.82;
					setNetAmount(parseFloat(calculatedNet.toFixed(2)));
				} else {
					// If initialPrice is invalid, leave fields empty
					setPrice('');
					setServiceFee('');
					setNetAmount('');
				}
			} else {
				// No initialPrice provided, leave fields empty
				setPrice('');
				setServiceFee('');
				setNetAmount('');
			}

			// Determine price option based on offer details
			if (isOneTime) {
				if (offerDetails) {
					if (offerDetails.is_free) {
						setSelectedPriceOption(PriceOption.FREE);
					} else if (offerDetails.is_default_price) {
						setSelectedPriceOption(PriceOption.FIXED);
					} else {
						setSelectedPriceOption(PriceOption.CUSTOM);
					}
				} else {
					// Default to fixed price for new offers
					setSelectedPriceOption(PriceOption.FIXED);
				}
			}

			// Set message from offer details if available
			if (offerDetails && offerDetails.offer_comment) {
				setMessage(offerDetails.offer_comment);
			}

			// Set default currency
			setSelectedCurrency(currency);

			// Clear message
			if (!offerDetails) {
				setMessage('');
			}
			
			// Clear error states
			setPriceError('');
			setNetAmountError('');
		}
	}, [isOpen, initialPrice, offerDetails, fixedPrice, currency, isOneTime]);

	// Handle price change
	const handlePriceChange = (value: string | number) => {
		// Always update the price state with the raw input for better UX
		setPrice(value);
		
		// If value is empty string, clear the derived values but show an error
		if (value === '' || value === null || value === undefined) {
			setServiceFee('');
			setNetAmount('');
			setPriceError(t('priceRequired', 'Price is required'));
			return;
		}
		
		const numericValue = typeof value === 'string' ? parseFloat(value) : value;
		
		// Validate price - ensure it's a number and greater than 0
		if (isNaN(numericValue) || numericValue <= 0) {
			setPriceError(t('priceMustBePositive', 'Price must be greater than 0'));
			setServiceFee('');
			setNetAmount('');
			return;
		} else {
			setPriceError('');
		}
		
		// Calculate derived values
		const serviceFeeAmount = numericValue * 0.18;
		const netAmountValue = numericValue - serviceFeeAmount;
		
		setServiceFee(parseFloat(serviceFeeAmount.toFixed(2)));
		setNetAmount(parseFloat(netAmountValue.toFixed(2)));
		setNetAmountError('');
	};
	
	// Update price when net amount changes
	const handleNetAmountChange = (value: string | number) => {
		// Always update the net amount state with the raw input for better UX
		setNetAmount(value);
		
		// If value is empty string, clear the related values but show an error
		if (value === '' || value === null || value === undefined) {
			setPrice('');
			setServiceFee('');
			setNetAmountError(t('netAmountRequired', 'Net amount is required'));
			return;
		}
		
		const numericValue = typeof value === 'string' ? parseFloat(value) : value;
		
		// Validate net amount
		if (isNaN(numericValue) || numericValue <= 0) {
			setNetAmountError(t('netAmountMustBePositive', 'Net amount must be greater than 0'));
			setPrice('');
			setServiceFee('');
			return;
		} else {
			setNetAmountError('');
		}
		
		// Calculate price based on net amount
		// If net amount is 82% of price (since fee is 18%), then:
		// netAmount = price * 0.82
		// price = netAmount / 0.82
		const calculatedPrice = numericValue / 0.82;
		
		// Then, calculate the service fee (18% of the calculated price)
		const calculatedFee = calculatedPrice * 0.18;
		
		// Round to 2 decimal places for better display
		setPrice(parseFloat(calculatedPrice.toFixed(2)));
		setServiceFee(parseFloat(calculatedFee.toFixed(2)));
		setPriceError('');
	};

	const handleSendOffer = async () => {
		if (isSending) return;
		
		// Validate price before sending
		const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
		
		// Check if price is empty for custom or seasonal offers
		if ((selectedPriceOption === PriceOption.CUSTOM || !isOneTime) && 
			(price === '' || price === null || price === undefined)) {
			setPriceError(t('priceRequired', 'Price is required'));
			return;
		}
		
		// Check if price is invalid (0 or negative)
		if ((selectedPriceOption === PriceOption.CUSTOM || !isOneTime) && 
			(isNaN(numericPrice) || numericPrice <= 0)) {
			setPriceError(t('priceMustBePositive', 'Price must be greater than 0'));
			return;
		}

		try {
			await onSendOffer({
				price: isOneTime
					? selectedPriceOption === PriceOption.FIXED
						? fixedPrice
						: selectedPriceOption === PriceOption.CUSTOM
							? numericPrice
							: 0
					: numericPrice, // For seasonal, use the price directly
				currency: isOneTime
					? selectedPriceOption === PriceOption.FIXED || selectedPriceOption === PriceOption.FREE
						? 'EUR'
						: selectedCurrency
					: selectedCurrency, // For seasonal, use the selected currency
				message,
				isFree: isOneTime ? selectedPriceOption === PriceOption.FREE : false,
				isFixedPrice: isOneTime ? selectedPriceOption === PriceOption.FIXED : false,
			});
		} catch (error) {
			console.error('Error sending offer:', error);
		}
	};

	const handleUpdateOffer = async () => {
		if (isSending || !onEditOffer) return;
		
		// Validate price before updating
		const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
		
		// Check if price is empty for custom or seasonal offers
		if ((selectedPriceOption === PriceOption.CUSTOM || !isOneTime) && 
			(price === '' || price === null || price === undefined)) {
			setPriceError(t('priceRequired', 'Price is required'));
			return;
		}
		
		// Check if price is invalid (0 or negative)
		if ((selectedPriceOption === PriceOption.CUSTOM || !isOneTime) && 
			(isNaN(numericPrice) || numericPrice <= 0)) {
			setPriceError(t('priceMustBePositive', 'Price must be greater than 0'));
			return;
		}

		try {
			await onEditOffer({
				price: isOneTime
					? selectedPriceOption === PriceOption.FIXED
						? fixedPrice
						: selectedPriceOption === PriceOption.CUSTOM
							? numericPrice
							: 0
					: numericPrice, // For seasonal, use the price directly
				currency: isOneTime
					? selectedPriceOption === PriceOption.FIXED || selectedPriceOption === PriceOption.FREE
						? 'EUR'
						: selectedCurrency
					: selectedCurrency, // For seasonal, use the selected currency
				message,
				isFree: isOneTime ? selectedPriceOption === PriceOption.FREE : false,
				isFixedPrice: isOneTime ? selectedPriceOption === PriceOption.FIXED : false,
			});
		} catch (error) {
			console.error('Error updating offer:', error);
		}
	};

	const handleCancelOffer = () => {
		setShowCancelConfirmation(true);
	};

	const confirmCancelOffer = async () => {
		if (isSending || !onCancelOffer) return;

		try {
			await onCancelOffer();
		} catch (error) {
			console.error('Error canceling offer:', error);
		}
	};

	const closeCancelConfirmation = () => {
		setShowCancelConfirmation(false);
	};

	const handleCurrencyChange = (event: SelectChangeEvent) => {
		setSelectedCurrency(event.target.value);
	};

	const renderOneTimeContent = () => (
		<Box className="flex flex-col gap-[24px]">
			<span className="text-secondary roboto-regular text-[16px] leading-normal mt-2">
				{t('farmerKnow')}
			</span>

			{/* Fixed Price Option (20 EUR) */}
			<Box
				className="flex flex-col gap-[12px] p-3 border rounded-lg"
				sx={{
					borderColor: selectedPriceOption === PriceOption.FIXED ? '#00AB89' : '#E9ECEF',
					backgroundColor:
						selectedPriceOption === PriceOption.FIXED ? 'rgba(0, 171, 137, 0.05)' : 'transparent',
				}}
			>
				<FormControlLabel
					control={
						<Checkbox
							checked={selectedPriceOption === PriceOption.FIXED}
							onChange={() => setSelectedPriceOption(PriceOption.FIXED)}
						/>
					}
					label={<Typography fontWeight="bold">{`${fixedPrice} EUR`}</Typography>}
				/>

				{selectedPriceOption === PriceOption.FIXED && (
					<Box className="ml-8 mt-1">
						<Box className="flex gap-[10px] mb-2">
							<Box sx={{ width: '50%' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', display: 'block' }}>
									{t('netAmount')}
								</Typography>
								<Typography fontWeight="medium">{(fixedPrice * 0.82).toFixed(1)}</Typography>
							</Box>
							<Box sx={{ width: '50%' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', display: 'block' }}>
									{t('serviceFee')}
									<Tooltip title={t('totalFee')} placement="top">
										<InfoOutlinedIcon
											fontSize="small"
											sx={{
												color: '#7B809A',
												fontSize: '16px',
												ml: '5px',
												verticalAlign: 'text-bottom',
											}}
										/>
									</Tooltip>
								</Typography>
								<Typography fontWeight="medium">{(fixedPrice * 0.18).toFixed(2)}</Typography>
							</Box>
						</Box>
						{/* Currency is fixed for fixed price option - no dropdown needed */}
						{!isEdit && (
							<Box sx={{ 
								width: '100%', 
								'& .quill': { 
									width: '100%',
									minHeight: '150px'
								},
								'& .ql-container': {
									minHeight: '100px'
								}
							}}>
								<Editor
									value={selectedPriceOption === PriceOption.FIXED ? message : ''}
									onChange={(value: string) => setMessage(value)}
									placeholder={t('addComment')}
									onBlur={() => {}}
								/>
							</Box>
						)}
					</Box>
				)}
			</Box>

			{/* Custom Price Option */}
			<Box
				className="flex flex-col gap-[12px] w-full p-3 border rounded-lg"
				sx={{
					borderColor: selectedPriceOption === PriceOption.CUSTOM ? '#00AB89' : '#E9ECEF',
					backgroundColor:
						selectedPriceOption === PriceOption.CUSTOM ? 'rgba(0, 171, 137, 0.05)' : 'transparent',
				}}
			>
				<FormControlLabel
					sx={{
						width: '100%',
					}}
					control={
						<Checkbox
							checked={selectedPriceOption === PriceOption.CUSTOM}
							onChange={() => setSelectedPriceOption(PriceOption.CUSTOM)}
						/>
					}
					label={
						<Box className="flex gap-[10px] w-full flex-1 items-center">
							<Box sx={{ width: '50%' }}>
								<Typography
									variant="caption"
									sx={{ fontSize: '12px', color: '#7B809A', mb: 1, display: 'block' }}
								>
									{t('price')}
								</Typography>
								<TextField
									fullWidth
									variant="outlined"
									size="small"
									type="number"
									value={price}
									onChange={(e) => {
										const value = e.target.value;
										handlePriceChange(value);
									}}
									disabled={selectedPriceOption !== PriceOption.CUSTOM}
									error={!!priceError && selectedPriceOption === PriceOption.CUSTOM}
									helperText={selectedPriceOption === PriceOption.CUSTOM ? priceError : ''}
									inputProps={{ min: 1, step: "0.01" }}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
										},
									}}
								/>
							</Box>
							<Box sx={{ width: '50%' }}>
								<Typography
									variant="caption"
									sx={{ fontSize: '12px', color: '#7B809A', mb: 1, display: 'block' }}
								>
									{t('unit')}
								</Typography>
								<FormControl fullWidth size="small">
									<Select
										value={selectedCurrency}
										onChange={handleCurrencyChange}
										disabled={selectedPriceOption !== PriceOption.CUSTOM}
										sx={{ borderRadius: '8px' }}
									>
										{CURRENCY_OPTIONS.map((option) => (
											<MenuItem key={option.value} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>
						</Box>
					}
				/>

				{selectedPriceOption === PriceOption.CUSTOM && (
					<Box className="ml-8 mt-1">
						{/* Add Net Amount and Service Fee UI for Custom Price Option */}
						<Box className="flex gap-[10px] mb-2">
							<Box sx={{ width: '50%' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', display: 'block' }}>
									{t('netAmount')}
								</Typography>
								<TextField
									fullWidth
									variant="outlined"
									size="small"
									type="number"
									value={netAmount}
									onChange={(e) => {
										const value = e.target.value;
										handleNetAmountChange(value);
									}}
									error={!!netAmountError}
									helperText={netAmountError}
									inputProps={{ min: 1, step: "0.01" }}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
										},
									}}
								/>
							</Box>
							<Box sx={{ width: '50%', position: 'relative' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
									{t('serviceFee')}
									<Tooltip title={t('totalFee')} placement="top">
										<InfoOutlinedIcon
											fontSize="small"
											sx={{
												color: '#7B809A',
												fontSize: '16px',
												ml: '5px',
												verticalAlign: 'text-bottom',
											}}
										/>
									</Tooltip>
								</Typography>
								<TextField
									fullWidth
									variant="outlined"
									size="small"
									type="number"
									value={serviceFee}
									disabled
									InputProps={{
										readOnly: true,
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
										},
										backgroundColor: '#F1F3F5',
									}}
								/>
							</Box>
						</Box>
						{!isEdit && (
							<Box sx={{ 
								width: '100%', 
								'& .quill': { 
									width: '100%',
									minHeight: '150px'
								},
								'& .ql-container': {
									minHeight: '100px'
								}
							}}>
								<Editor
									value={selectedPriceOption === PriceOption.CUSTOM ? message : ''}
									onChange={(value: string) => setMessage(value)}
									placeholder={t('addComment')}
									onBlur={() => {}}
								/>
							</Box>
						)}
					</Box>
				)}
			</Box>

			{/* Free Consultation Option */}
			<Box
				className="flex flex-col gap-[12px] p-3 border rounded-lg"
				sx={{
					borderColor: selectedPriceOption === PriceOption.FREE ? '#00AB89' : '#E9ECEF',
					backgroundColor:
						selectedPriceOption === PriceOption.FREE ? 'rgba(0, 171, 137, 0.05)' : 'transparent',
				}}
			>
				<FormControlLabel
					control={
						<Checkbox
							checked={selectedPriceOption === PriceOption.FREE}
							onChange={() => setSelectedPriceOption(PriceOption.FREE)}
						/>
					}
					label={
						<Box
							sx={{
								backgroundColor: '#FFF9EB',
								color: '#F68B1F',
								borderRadius: '4px',
								padding: '4px 10px',
								display: 'inline-flex',
								fontWeight: 'bold',
								alignItems: 'center',
								gap: '5px',
							}}
						>
							{t('freeConsultations')}
							<Tooltip title={t('expertiseNoCharge')} arrow>
								<InfoOutlinedIcon fontSize="small" />
							</Tooltip>
						</Box>
					}
				/>

				{selectedPriceOption === PriceOption.FREE && (
					<Box className="ml-8 mt-1">
						{/* Currency is fixed for free consultations - no dropdown needed */}
						{!isEdit && (
							<Box sx={{ 
								width: '100%', 
								'& .quill': { 
									width: '100%',
									minHeight: '150px'
								},
								'& .ql-container': {
									minHeight: '100px'
								}
							}}>
								<Editor
									value={selectedPriceOption === PriceOption.FREE ? message : ''}
									onChange={(value: string) => setMessage(value)}
									placeholder={t('addComment')}
									onBlur={() => {}}
								/>
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);

	const renderSeasonalContent = () => (
		<Box className="flex flex-col gap-[18px]">
			<span className="text-secondary roboto-regular text-[16px] leading-normal mt-2">
				{t('farmerKnow')}
			</span>

			{/* Price and Currency Inputs */}
			<Box className="flex gap-[10px]">
				<Box sx={{ width: '50%' }}>
					<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
						{t('price')}
					</Typography>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						type="number"
						value={price}
						onChange={(e) => {
							const value = e.target.value;
							handlePriceChange(value);
						}}
						error={!!priceError}
						helperText={priceError}
						inputProps={{ min: 1, step: "0.01" }}
						sx={{
							'& .MuiOutlinedInput-root': {
								borderRadius: '8px',
							},
						}}
					/>
				</Box>
				<Box sx={{ width: '50%' }}>
					<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
						{t('unit')}
					</Typography>
					<FormControl fullWidth size="small">
						<Select
							value={selectedCurrency}
							onChange={handleCurrencyChange}
							sx={{ borderRadius: '8px' }}
						>
							{CURRENCY_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			</Box>

			{/* Net Amount and Service Fee */}
			<Box className="flex gap-[10px]">
				<Box sx={{ width: '50%' }}>
					<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
						{t('netAmount')}
					</Typography>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						type="number"
						value={netAmount}
						onChange={(e) => handleNetAmountChange(e.target.value)}
						error={!!netAmountError}
						helperText={netAmountError}
						inputProps={{ min: 1 }}
						sx={{
							'& .MuiOutlinedInput-root': {
								borderRadius: '8px',
							},
						}}
					/>
				</Box>
				<Box sx={{ width: '50%', position: 'relative' }}>
					<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
						{t('serviceFee')}
						<Tooltip title={t('totalFee')} placement="top">
							<InfoOutlinedIcon
								fontSize="small"
								sx={{
									color: '#7B809A',
									fontSize: '16px',
									ml: '5px',
									verticalAlign: 'text-bottom',
								}}
							/>
						</Tooltip>
					</Typography>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						type="number"
						value={serviceFee}
						disabled
						sx={{
							'& .MuiOutlinedInput-root': {
								borderRadius: '8px',
							},
							backgroundColor: '#F1F3F5',
						}}
					/>
				</Box>
			</Box>

			{/* Comment Editor */}
			{!isEdit && (
				<Box sx={{ mt: 1, width: '100%' }}>
					<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
						{t('comment')} ({t('optional')})
					</Typography>
					<Box sx={{ 
						width: '100%', 
						'& .quill': { 
							width: '100%',
							minHeight: '150px'
						},
						'& .ql-container': {
							minHeight: '100px'
						}
					}}>
						<Editor
							value={message}
							onChange={(value: string) => setMessage(value)}
							placeholder={t('addComment')}
							onBlur={() => {}}
						/>
					</Box>
					{/* Character count removed as requested */}
				</Box>
			)}
		</Box>
	);

	return (
		<>
			<GeneralMuiModal
				open={isOpen}
				onClose={onClose}
				maxWidth="xl"
				title={isEdit ? t('editOffer') : t('sendOffer')}
				styles={{
					paperStyle: { width: '560px', maxWidth: '100%' },
					actionsStyle: { justifyContent: 'space-between' },
				}}
				content={
					isSending && !offerDetails ? (
						<Box className="flex justify-center items-center py-8">
							<CircularProgress />
						</Box>
					) : isOneTime ? (
						renderOneTimeContent()
					) : (
						renderSeasonalContent()
					)
				}
				actions={
					<>
						{isEdit ? (
							<div className="flex justify-end items-center gap-5 w-full">
								<Button
									variant="outlined"
									onClick={handleCancelOffer}
									disabled={isSending}
									sx={{
										padding: '10px 20px',
										fontWeight: '700',
										fontSize: '14px',
										backgroundColor: '#FD3333',
										color: 'white',
										border: 'unset',
										'&:hover': {
											backgroundColor: '#D32F2F',
										},
										'@media (max-width: 375px)': {
											padding: '10px',
										},
									}}
								>
									{t('cancelOffer')}
								</Button>
								<Button
									variant="contained"
									onClick={handleUpdateOffer}
									disabled={isSending || !!priceError || !!netAmountError}
									sx={{
										padding: '10px 20px',
										fontWeight: '700',
										fontSize: '14px',
										backgroundColor: '#00AB89',
										'&:hover': {
											backgroundColor: '#008C6F',
										},
										'@media (max-width: 375px)': {
											padding: '10px',
										},
									}}
								>
									{isSending ? <CircularProgress size={24} color="inherit" /> : t('editOffer')}
								</Button>
							</div>
						) : (
							<>
								<Button
									variant="outlined"
									onClick={onChat}
									startIcon={<QuestionAnswerOutlinedIcon />}
									disabled={isSending}
									sx={{
										padding: '10px 20px',
										fontWeight: '700',
										fontSize: '14px',
										color: '#7B809A',
										border: 'unset',
										boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.25)',
										'&:hover': {
											backgroundColor: '#f5f5f5',
										},
										'@media (max-width: 375px)': {
											padding: '10px',
										},
									}}
								>
									<span className="text-dark">{t('chat')}</span>
								</Button>
								<Button
									variant="contained"
									onClick={handleSendOffer}
									disabled={isSending || !!priceError || !!netAmountError}
									sx={{
										padding: '10px 20px',
										fontWeight: '700',
										fontSize: '14px',
										backgroundColor: '#00AB89',
										'&:hover': {
											backgroundColor: '#008C6F',
										},
										'@media (max-width: 375px)': {
											padding: '10px',
										},
									}}
								>
									{isSending ? <CircularProgress size={24} color="inherit" /> : t('sendOffer')}
								</Button>
							</>
						)}
					</>
				}
			/>

			{/* Cancel Confirmation Dialog */}
			<Dialog
				open={showCancelConfirmation}
				onClose={closeCancelConfirmation}
				aria-labelledby="cancel-offer-dialog-title"
				PaperProps={{
					sx: {
						borderRadius: '8px',
						width: '100%',
						maxWidth: '400px',
					},
				}}
			>
				<DialogTitle id="cancel-offer-dialog-title" sx={{ pb: 1 }}>
					{t('cancelOffer')}
					<IconButton
						aria-label="close"
						onClick={closeCancelConfirmation}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<DialogContentText>{t('areYouSureYouWantToCancelTheOffer')}</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3, gap: 2 }}>
					<Button
						onClick={closeCancelConfirmation}
						variant="outlined"
						sx={{
							width: '100px',
							borderColor: '#ccc',
							color: '#333',
							borderRadius: '4px',
							'&:hover': {
								backgroundColor: '#f5f5f5',
								borderColor: '#bbb',
							},
						}}
					>
						{t('no')}
					</Button>
					<Button
						onClick={confirmCancelOffer}
						disabled={isSending}
						variant="contained"
						sx={{
							width: '100px',
							backgroundColor: '#F44336',
							color: 'white',
							borderRadius: '4px',
							'&:hover': {
								backgroundColor: '#D32F2F',
							},
						}}
					>
						{isSending ? <CircularProgress size={20} color="inherit" /> : t('yes')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
